"use client";

import { useRef, useCallback } from "react";

export interface LongPressOptions {
  /** Total duration in ms to hold before triggering (default: 3000) */
  duration?: number;
  /** Called on every animation frame while holding, with progress 0→1 */
  onProgress?: (progress: number) => void;
  /** Called once hold completes */
  onComplete: () => void;
  /** Called if hold is released early */
  onCancel?: () => void;
}

export function useLongPress({
  duration = 3000,
  onProgress,
  onComplete,
  onCancel,
}: LongPressOptions) {
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const completedRef = useRef(false);

  const cancel = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (startTimeRef.current !== null && !completedRef.current) {
      onCancel?.();
    }
    startTimeRef.current = null;
    completedRef.current = false;
  }, [onCancel]);

  const start = useCallback(() => {
    // Don't start if already running
    if (startTimeRef.current !== null) return;
    completedRef.current = false;
    startTimeRef.current = performance.now();

    const tick = (now: number) => {
      if (startTimeRef.current === null) return;
      const elapsed = now - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      onProgress?.(progress);

      if (progress >= 1) {
        completedRef.current = true;
        startTimeRef.current = null;
        rafRef.current = null;
        // Haptic feedback: short double-buzz on complete
        if (typeof navigator !== "undefined" && "vibrate" in navigator) {
          navigator.vibrate([60, 40, 60]);
        }
        onComplete();
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [duration, onProgress, onComplete]);

  const handlers = {
    onPointerDown: (e: React.PointerEvent) => {
      // Primary button only (left click or finger)
      if (e.pointerType === "mouse" && e.button !== 0) return;
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      start();
    },
    onPointerUp: cancel,
    onPointerLeave: cancel,
    onPointerCancel: cancel,
    // Block context menu on long-press on mobile
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  };

  return { handlers, cancel };
}
